import React, { useState, useEffect } from 'react';
import {
  Flame,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  MapPin,
  User,
  Phone,
  Mail,
  MessageSquare,
  Settings,
  Bell,
  CheckCircle2,
  AlertCircle,
  Clock,
  Euro,
  Search,
  Filter,
  X,
  Save,
  ChevronDown,
  ChevronRight,
  Thermometer,
  Wrench,
  Shield,
  FileText,
  Send,
  ArrowRight,
  ArrowLeft,
  Building2,
  Zap
} from 'lucide-react';
import {
  fetchLocations,
  updateLocation,
  getCurrentUserCompany,
  type Location,
  type Company
} from '../lib/supabase';

interface MaintenanceContract {
  id: string;
  location_id: string;
  location: Location;
  contract_type: 'gas' | 'mazout' | 'heat_pump';
  frequency: number; // months
  price: number;
  last_maintenance: string | null;
  next_maintenance: string | null;
  is_active: boolean;
  property_manager: string;
  device_details: {
    brand?: string;
    model?: string;
    installation_year?: number;
    serial_number?: string;
    capacity?: string;
  };
  notifications: {
    email: boolean;
    sms: boolean;
    days_before: number;
  };
  chat_messages: ChatMessage[];
}

interface ChatMessage {
  id: string;
  sender: string;
  message: string;
  timestamp: string;
  type: 'customer' | 'technician' | 'system';
}

const contractTypeConfig = {
  gas: {
    name: 'Gas Heating',
    icon: Flame,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-200',
    price: 6.99,
    frequency: 24
  },
  mazout: {
    name: 'Oil Heating',
    icon: Thermometer,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200',
    price: 17.99,
    frequency: 12
  },
  heat_pump: {
    name: 'Heat Pump',
    icon: Wrench,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    price: 6.99,
    frequency: 24
  }
};

const mockChatMessages: ChatMessage[] = [
  {
    id: '1',
    sender: 'Technician Mike',
    message: 'Annual maintenance completed successfully. All systems running optimally.',
    timestamp: '2025-01-15T10:30:00Z',
    type: 'technician'
  },
  {
    id: '2',
    sender: 'System',
    message: 'Next maintenance scheduled for January 2026',
    timestamp: '2025-01-15T10:35:00Z',
    type: 'system'
  }
];

export function HeatingComfortPro() {
  const [contracts, setContracts] = useState<MaintenanceContract[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<MaintenanceContract | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [expandedContract, setExpandedContract] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    location_id: '',
    contract_type: 'gas' as 'gas' | 'mazout' | 'heat_pump',
    frequency: 24,
    price: 6.99,
    last_maintenance: '',
    property_manager: '',
    device_brand: '',
    device_model: '',
    device_year: '',
    device_serial: '',
    device_capacity: '',
    notifications_email: true,
    notifications_sms: false,
    notifications_days: 30
  });

  useEffect(() => {
    loadData();
  }, []);

  // Update price and frequency when contract type changes
  useEffect(() => {
    const config = contractTypeConfig[formData.contract_type];
    setFormData(prev => ({
      ...prev,
      price: config.price,
      frequency: config.frequency
    }));
  }, [formData.contract_type]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const currentCompany = await getCurrentUserCompany();
      if (!currentCompany) {
        throw new Error('No company found');
      }
      setCompany(currentCompany);

      // Load all locations for the company
      const locations = await fetchLocations(currentCompany.id);
      setAllLocations(locations);

      // Load locations with active maintenance contracts
      const activeContracts = locations
        .filter(location => location.maintenance_contract_active)
        .map(location => ({
          id: location.id,
          location_id: location.id,
          location,
          contract_type: location.maintenance_contract_type!,
          frequency: location.maintenance_contract_frequency || 12,
          price: location.maintenance_contract_price || 0,
          last_maintenance: location.last_maintenance,
          next_maintenance: location.next_maintenance,
          is_active: location.maintenance_contract_active,
          property_manager: location.contact_person_name, // Use contact person as property manager
          device_details: {
            brand: 'Viessmann', // Mock data - would come from database
            model: 'Vitodens 200-W',
            installation_year: 2020,
            serial_number: 'VD200W-2020-001',
            capacity: '24 kW'
          },
          notifications: {
            email: location.communication_preferences.email,
            sms: location.communication_preferences.sms,
            days_before: 30
          },
          chat_messages: mockChatMessages
        }));

      setContracts(activeContracts);
    } catch (err) {
      console.error('Error loading HeatingComfort data:', err);
      setError('Failed to load maintenance contracts');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateNextMaintenance = (lastMaintenance: string, frequency: number) => {
    if (!lastMaintenance) return null;
    const lastDate = new Date(lastMaintenance);
    const nextDate = new Date(lastDate);
    nextDate.setMonth(nextDate.getMonth() + frequency);
    return nextDate.toISOString().split('T')[0];
  };

  const handleCreateContract = async () => {
    try {
      if (!formData.location_id) {
        setValidationError('Please select a location');
        return;
      }

      const nextMaintenance = formData.last_maintenance 
        ? calculateNextMaintenance(formData.last_maintenance, formData.frequency)
        : null;

      await updateLocation(formData.location_id, {
        maintenance_contract_type: formData.contract_type,
        maintenance_contract_frequency: formData.frequency,
        maintenance_contract_price: formData.price,
        maintenance_contract_active: true,
        last_maintenance: formData.last_maintenance || null,
        next_maintenance: nextMaintenance,
        communication_preferences: {
          email: formData.notifications_email,
          sms: formData.notifications_sms,
          phone: false
        }
      });

      await loadData();
      setIsCreateModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error creating maintenance contract:', error);
      setError('Error creating maintenance contract: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const resetForm = () => {
    setFormData({
      location_id: '',
      contract_type: 'gas',
      frequency: 24,
      price: 6.99,
      last_maintenance: '',
      property_manager: '',
      device_brand: '',
      device_model: '',
      device_year: '',
      device_serial: '',
      device_capacity: '',
      notifications_email: true,
      notifications_sms: false,
      notifications_days: 30
    });
    setCurrentStep(1);
    setValidationError(null);
  };

  const handleSendMessage = (contractId: string) => {
    if (!newMessage.trim()) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      sender: 'You',
      message: newMessage.trim(),
      timestamp: new Date().toISOString(),
      type: 'customer'
    };

    setContracts(prev => prev.map(contract => 
      contract.id === contractId 
        ? { ...contract, chat_messages: [...contract.chat_messages, message] }
        : contract
    ));

    setNewMessage('');
  };

  const getMaintenanceStatus = (contract: MaintenanceContract) => {
    if (!contract.next_maintenance) return { status: 'unknown', color: 'text-gray-500', label: 'Not scheduled' };
    
    const nextDate = new Date(contract.next_maintenance);
    const today = new Date();
    const daysUntil = Math.ceil((nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      return { status: 'overdue', color: 'text-red-600', label: `${Math.abs(daysUntil)} days overdue` };
    } else if (daysUntil <= 30) {
      return { status: 'due_soon', color: 'text-orange-600', label: `Due in ${daysUntil} days` };
    } else {
      return { status: 'scheduled', color: 'text-green-600', label: `Due in ${daysUntil} days` };
    }
  };

  // Get locations that don't have active maintenance contracts
  const availableLocations = allLocations.filter(location => 
    !location.maintenance_contract_active
  );

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = 
      contract.location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.location.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.property_manager.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterType === 'all' || contract.contract_type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.location_id) {
        setValidationError('Please select a location');
        return;
      }
      
      // Pre-fill property manager with location contact person
      const selectedLocation = allLocations.find(loc => loc.id === formData.location_id);
      if (selectedLocation) {
        setFormData(prev => ({
          ...prev,
          property_manager: selectedLocation.contact_person_name
        }));
      }
      
      setValidationError(null);
    }
    
    if (currentStep === 2) {
      if (!formData.last_maintenance) {
        setValidationError('Please enter the last maintenance date');
        return;
      }
      if (!formData.property_manager) {
        setValidationError('Please enter a property manager');
        return;
      }
      setValidationError(null);
    }
    
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setValidationError(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading HeatingComfort Pro...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <Flame className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">HeatingComfort Pro</h1>
              <p className="text-gray-600">Centralized maintenance contract management</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder="Search contracts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="gas">Gas Heating</option>
            <option value="mazout">Oil Heating</option>
            <option value="heat_pump">Heat Pump</option>
          </select>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Plus className="h-5 w-5" />
            <span>New Contract</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Contracts Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Contracts</p>
              <p className="text-2xl font-bold text-gray-900">{contracts.length}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Due Soon</p>
              <p className="text-2xl font-bold text-orange-900">
                {contracts.filter(c => getMaintenanceStatus(c).status === 'due_soon').length}
              </p>
            </div>
            <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-red-900">
                {contracts.filter(c => getMaintenanceStatus(c).status === 'overdue').length}
              </p>
            </div>
            <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Monthly Cost</p>
              <p className="text-2xl font-bold text-green-900">
                €{contracts.reduce((sum, c) => sum + c.price, 0).toFixed(2)}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Euro className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Contracts List */}
      <div className="space-y-4">
        {filteredContracts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Flame className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Maintenance Contracts</h3>
            <p className="text-gray-600 mb-6">
              {contracts.length === 0 
                ? 'Get started by creating your first HeatingComfort maintenance contract.'
                : 'No contracts match your current search criteria.'
              }
            </p>
            {contracts.length === 0 && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="gradient-bg text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
              >
                Create First Contract
              </button>
            )}
          </div>
        ) : (
          filteredContracts.map((contract) => {
            const config = contractTypeConfig[contract.contract_type];
            const status = getMaintenanceStatus(contract);
            const isExpanded = expandedContract === contract.id;
            
            return (
              <div key={contract.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Contract Header */}
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`h-12 w-12 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                        <config.icon className={`h-6 w-6 ${config.color}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{contract.location.name}</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {contract.location.address}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {contract.property_manager}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${status.color}`}>
                            {status.label}
                          </span>
                          {status.status === 'overdue' && <AlertCircle className="h-4 w-4 text-red-600" />}
                          {status.status === 'due_soon' && <Clock className="h-4 w-4 text-orange-600" />}
                          {status.status === 'scheduled' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                        </div>
                        <div className="text-sm text-gray-600">
                          €{contract.price.toFixed(2)} / month
                        </div>
                      </div>
                      
                      <button
                        onClick={() => setExpandedContract(isExpanded ? null : contract.id)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50">
                    <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Device Details */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Settings className="h-5 w-5" />
                          Device Details
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm text-gray-600">Brand & Model</label>
                            <p className="font-medium">{contract.device_details.brand} {contract.device_details.model}</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600">Installation Year</label>
                            <p className="font-medium">{contract.device_details.installation_year}</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600">Capacity</label>
                            <p className="font-medium">{contract.device_details.capacity}</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600">Serial Number</label>
                            <p className="font-medium text-xs">{contract.device_details.serial_number}</p>
                          </div>
                        </div>
                      </div>

                      {/* Maintenance Schedule */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Calendar className="h-5 w-5" />
                          Maintenance Schedule
                        </h4>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm text-gray-600">Last Maintenance</label>
                            <p className="font-medium">
                              {contract.last_maintenance 
                                ? new Date(contract.last_maintenance).toLocaleDateString()
                                : 'Not recorded'
                              }
                            </p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600">Next Maintenance</label>
                            <p className={`font-medium ${status.color}`}>
                              {contract.next_maintenance 
                                ? new Date(contract.next_maintenance).toLocaleDateString()
                                : 'Not scheduled'
                              }
                            </p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600">Frequency</label>
                            <p className="font-medium">Every {contract.frequency} months</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600">Property Manager</label>
                            <p className="font-medium">{contract.property_manager}</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-600">Notifications</label>
                            <div className="flex items-center gap-2">
                              {contract.notifications.email && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                  <Mail className="h-3 w-3 mr-1" />
                                  Email
                                </span>
                              )}
                              {contract.notifications.sms && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                  <Phone className="h-3 w-3 mr-1" />
                                  SMS
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Communication */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <MessageSquare className="h-5 w-5" />
                          Communication
                        </h4>
                        
                        {/* Recent Messages */}
                        <div className="space-y-3 max-h-48 overflow-y-auto">
                          {contract.chat_messages.slice(-3).map((message) => (
                            <div key={message.id} className="bg-white rounded-lg p-3 border">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900">{message.sender}</span>
                                <span className="text-xs text-gray-500">
                                  {new Date(message.timestamp).toLocaleDateString()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{message.message}</p>
                            </div>
                          ))}
                        </div>

                        {/* Message Input */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Send a message..."
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent text-sm"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleSendMessage(contract.id);
                              }
                            }}
                          />
                          <button
                            onClick={() => handleSendMessage(contract.id)}
                            className="gradient-bg text-white p-2 rounded-lg hover:opacity-90 transition-opacity"
                          >
                            <Send className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Create Contract Modal - Multi-step */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Create Maintenance Contract</h2>
                <p className="text-gray-600 text-sm mt-1">Step {currentStep} of 3</p>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Progress Steps */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className={`flex items-center ${currentStep >= 1 ? 'text-jaimy-red' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= 1 ? 'bg-jaimy-red text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    1
                  </div>
                  <span className="ml-2 text-sm font-medium">Location</span>
                </div>
                <div className={`w-12 h-1 ${currentStep >= 2 ? 'bg-jaimy-red' : 'bg-gray-200'}`}></div>
                <div className={`flex items-center ${currentStep >= 2 ? 'text-jaimy-red' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= 2 ? 'bg-jaimy-red text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    2
                  </div>
                  <span className="ml-2 text-sm font-medium">Details</span>
                </div>
                <div className={`w-12 h-1 ${currentStep >= 3 ? 'bg-jaimy-red' : 'bg-gray-200'}`}></div>
                <div className={`flex items-center ${currentStep >= 3 ? 'text-jaimy-red' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= 3 ? 'bg-jaimy-red text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    3
                  </div>
                  <span className="ml-2 text-sm font-medium">Confirm</span>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {/* Validation Error */}
              {validationError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-red-700 text-sm">{validationError}</p>
                </div>
              )}
              
              {/* Step 1: Location Selection */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Select Location
                  </h3>
                  
                  {availableLocations.length === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <AlertCircle className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                      <p className="text-yellow-800 font-medium">No Available Locations</p>
                      <p className="text-yellow-700 text-sm mt-1">
                        All locations already have active maintenance contracts.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3">
                      {availableLocations.map((location) => (
                        <label 
                          key={location.id}
                          className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                            formData.location_id === location.id
                              ? 'border-jaimy-red bg-jaimy-red/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="location"
                            value={location.id}
                            checked={formData.location_id === location.id}
                            onChange={() => setFormData(prev => ({ ...prev, location_id: location.id }))}
                            className="mt-1 text-jaimy-red focus:ring-jaimy-red"
                          />
                          <div className="ml-3">
                            <div className="font-medium text-gray-900">{location.name}</div>
                            <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                              <MapPin className="h-4 w-4" />
                              {location.address}
                            </div>
                            <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                              <Building2 className="h-4 w-4" />
                              {location.type.replace('_', ' ')}
                            </div>
                            <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                              <User className="h-4 w-4" />
                              {location.contact_person_name} ({location.contact_person_role})
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Step 2: Contract Details */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Contract Details
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Heating System Type *
                      </label>
                      <select
                        value={formData.contract_type}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contract_type: e.target.value as 'gas' | 'mazout' | 'heat_pump'
                        }))}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                        required
                      >
                        <option value="gas">Gas Heating (€6.99/month, 24 months)</option>
                        <option value="mazout">Oil Heating (€17.99/month, 12 months)</option>
                        <option value="heat_pump">Heat Pump (€6.99/month, 24 months)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Maintenance Date *
                      </label>
                      <input
                        type="date"
                        value={formData.last_maintenance}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_maintenance: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Property Manager *
                      </label>
                      <input
                        type="text"
                        value={formData.property_manager}
                        onChange={(e) => setFormData(prev => ({ ...prev, property_manager: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                        placeholder="Name of property manager"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-blue-800 mb-2">
                      <Zap className="h-5 w-5" />
                      <span className="font-medium">24/7 Heating Assistance Hotline Included</span>
                    </div>
                    <p className="text-blue-700 text-sm">
                      This contract includes access to our 24/7 emergency heating assistance hotline for immediate support in case of heating system failures.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Device Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand
                        </label>
                        <input
                          type="text"
                          value={formData.device_brand}
                          onChange={(e) => setFormData(prev => ({ ...prev, device_brand: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                          placeholder="e.g., Viessmann, Buderus"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Model
                        </label>
                        <input
                          type="text"
                          value={formData.device_model}
                          onChange={(e) => setFormData(prev => ({ ...prev, device_model: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                          placeholder="e.g., Vitodens 200-W"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Installation Year
                        </label>
                        <input
                          type="text"
                          value={formData.device_year}
                          onChange={(e) => setFormData(prev => ({ ...prev, device_year: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                          placeholder="e.g., 2020"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Capacity
                        </label>
                        <input
                          type="text"
                          value={formData.device_capacity}
                          onChange={(e) => setFormData(prev => ({ ...prev, device_capacity: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                          placeholder="e.g., 24 kW"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 3: Confirmation */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Confirm Contract Details
                  </h3>
                  
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">Location</h4>
                    <div className="space-y-2">
                      {formData.location_id && (
                        <div>
                          <p className="font-medium text-gray-900">
                            {allLocations.find(l => l.id === formData.location_id)?.name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {allLocations.find(l => l.id === formData.location_id)?.address}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">Contract Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Heating System Type</p>
                        <p className="font-medium text-gray-900">
                          {contractTypeConfig[formData.contract_type].name}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Last Maintenance</p>
                        <p className="font-medium text-gray-900">
                          {formData.last_maintenance ? new Date(formData.last_maintenance).toLocaleDateString() : 'Not set'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Next Maintenance</p>
                        <p className="font-medium text-gray-900">
                          {formData.last_maintenance 
                            ? new Date(calculateNextMaintenance(formData.last_maintenance, formData.frequency) || '').toLocaleDateString() 
                            : 'Not scheduled'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Property Manager</p>
                        <p className="font-medium text-gray-900">{formData.property_manager}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                      <Euro className="h-5 w-5" />
                      Pricing Details
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-blue-800">Monthly fee:</span>
                        <span className="font-medium text-blue-900">€{formData.price.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-800">Maintenance frequency:</span>
                        <span className="font-medium text-blue-900">Every {formData.frequency} months</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-800">24/7 Heating assistance:</span>
                        <span className="font-medium text-blue-900">Included</span>
                      </div>
                      <div className="flex justify-between border-t border-blue-200 pt-2 mt-2">
                        <span className="font-medium text-blue-900">Total annual cost:</span>
                        <span className="font-bold text-blue-900">€{(formData.price * 12).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Notifications
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <CheckCircle2 className={`h-5 w-5 ${formData.notifications_email ? 'text-green-600' : 'text-gray-300'}`} />
                        <span className="ml-2 text-gray-700">Email notifications</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle2 className={`h-5 w-5 ${formData.notifications_sms ? 'text-green-600' : 'text-gray-300'}`} />
                        <span className="ml-2 text-gray-700">SMS notifications</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-2">
                        Notifications will be sent {formData.notifications_days} days before scheduled maintenance.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-100 flex justify-between">
              {currentStep > 1 ? (
                <button
                  onClick={prevStep}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              ) : (
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              )}
              
              {currentStep < 3 ? (
                <button
                  onClick={nextStep}
                  className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleCreateContract}
                  className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Create Contract
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}